import type { DeliveryInitialValues } from "@lib/utils/checkoutHelper";

// List all possible outcome codes from the address validation service
export const ADDRESS_VALIDATION_CODES = {
    /**
     * Indicates that the address validation service has suggested a different address.
     */
    ADDRESS_SUGGESTION: "ADDRESS_SUGGESTION",
    /**
     * Indicates that an unknown error occurred during address validation.
     */
    UNKNOWN_ERROR: "UNKNOWN_ERROR",
    /**
     * Indicates that the address is invalid without further information. Ideally we should have a more precise response.
     */
    INVALID_ADDRESS: "INVALID_ADDRESS",
    /**
     * Indicates a warning from the address validation service.
     */
    WARNING: "WARNING",
    /**
     * Indicates that the entered street contains no house number.
     */
    MISSING_HOUSE_NUMBER: "MISSING_HOUSE_NUMBER",
} as const;

export type AddressValidationCode =
    (typeof ADDRESS_VALIDATION_CODES)[keyof typeof ADDRESS_VALIDATION_CODES];

/**
 * Types of actions that can be taken in response to address validation results.
 * "modal" indicates that a modal should be shown to the user for further action.
 * "error" indicates that an error has occurred and no further action should be taken. The flow is stopped.
 */
export type AddressValidationAction = "modal" | "error";

/**
 * Error type indicating that the address validation service has suggested a different address.
 */
export type AddressSuggestionError = {
    type: typeof ADDRESS_VALIDATION_CODES.ADDRESS_SUGGESTION;
    data: {
        addressSuggestion: DeliveryInitialValues;
    };
};

/**
 * Error type indicating a warning from the address validation service.
 */
export type WarningAddressError = {
    type: typeof ADDRESS_VALIDATION_CODES.WARNING;
};
/**
 * Error type indicating that an unknown error occurred during address validation.
 */
export type UnknownAddressError = {
    type: typeof ADDRESS_VALIDATION_CODES.UNKNOWN_ERROR;
};

/**
 * Error type indicating a the address is invalid without further information. Ideally we should have a more precise response.
 */
export type InvalidAddressError = {
    type: typeof ADDRESS_VALIDATION_CODES.INVALID_ADDRESS;
};

/**
 * Error type indicating that the entered street has no house number in it.
 */
export type MissingHouseNumberError = {
    type: typeof ADDRESS_VALIDATION_CODES.MISSING_HOUSE_NUMBER;
};

// Union type of all possible error results
export type AddressValidatorError =
    | AddressSuggestionError
    | UnknownAddressError
    | InvalidAddressError
    | WarningAddressError
    | MissingHouseNumberError;

export type AddressValidatorSuccessResult = {
    isValid: true;
};
export type AddressValidatorFailureResult = {
    isValid: false;
    action?: AddressValidationAction;
    error: AddressValidatorError;
};

export type CheckoutType = "express" | "regular";

export type AddressValidatorResult = AddressValidatorSuccessResult | AddressValidatorFailureResult;

export type AddressValidatorFunction = ({
    address,
    shippingMethodId,
    checkoutType,
}: {
    address: DeliveryInitialValues;
    shippingMethodId: string;
    checkoutType: CheckoutType;
}) => Promise<AddressValidatorResult>;
